import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Event } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  CarouselStep,
  ScreenBreakpoints,
  createDoubleCardSlides,
  CarouselSlideMultipleSteps,
  ScreenSizeEnums,
  StepOtherOption,
} from './carousel.interfaces';
import { ClaimingStage, ClaimingDialogActions } from './../../../api/models/claiming.models';

/**
 * Wrapper of the Carousel component
 * @Input autoPlay sets the carousel autoplay. Default if FALSE
 * @Input showIndicators sets the flag to display the carousel circle indicators. Default is TRUE
 * @Input carouselSlides is the list of carousel slides, which follows the interface CarouselStep.
 * The carousel slides will adapt to the number os slides that receives from the input, however, it is not advised to use to display more than 5-6 cards
 * @Output startButtonClick emits the start button click event
 */
@Component({
  selector: 'carousel',
  templateUrl: './carousel.component.html',
  standalone: false,
})
export class CarouselComponent implements OnInit, OnDestroy, OnChanges {
  private readonly carousel$: Subject<void> = new Subject<void>();
  @Input() autoPlay = false;
  @Input() showIndicators = true;
  @Input() carouselSlides: CarouselStep[];
  @Input() nextButtonText: string;
  @Input() previousButtonText: string;
  @Input() startButtonText: string;
  @Input() otherOptions: StepOtherOption[];
  @Input() claimingStage: ClaimingStage;
  @Output() startButtonClick = new EventEmitter<any>();
  @Output() claimingDialogActionEmitter = new EventEmitter<ClaimingDialogActions>();
  carouselSlidesMultipleSteps: CarouselSlideMultipleSteps[];
  currentSlideIndex = 0;
  mouseClick: Event;
  screenSize: ScreenSizeEnums;

  constructor(private readonly breakpointObserver: BreakpointObserver) {}

  ngOnInit() {
    this.breakpointObserver
      .observe([ScreenBreakpoints.sm, ScreenBreakpoints.xl])
      .pipe(takeUntil(this.carousel$))
      .subscribe(result => {
        const isLargeScreen = result.breakpoints[ScreenBreakpoints.xl];
        const isSmallScreen = result.breakpoints[ScreenBreakpoints.sm];

        if (isLargeScreen) {
          this.screenSize = ScreenSizeEnums.LargeScreen;
        } else {
          this.screenSize = isSmallScreen ? ScreenSizeEnums.SmallScreen : ScreenSizeEnums.MediumScreen;
        }
      });

    this.carouselSlidesMultipleSteps = createDoubleCardSlides(this.carouselSlides);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Resets the index when the slides changes
    const { carouselSlides, claimingStage } = changes;
    const { previousValue, currentValue } = carouselSlides;
    const hasSlides = carouselSlides && previousValue && currentValue && this.carouselSlides.length > 1;
    const lengthChanged = previousValue?.length !== currentValue?.length;
    const stageChanged = claimingStage.previousValue !== claimingStage.currentValue;
    if ((hasSlides && lengthChanged) || (hasSlides && stageChanged)) {
      this.carouselSlidesMultipleSteps = createDoubleCardSlides(this.carouselSlides);
      this.currentSlideIndex = 0;
    }
  }

  ngOnDestroy() {
    this.carousel$.unsubscribe();
  }

  handleStartButtonClick(clicked: boolean): void {
    this.startButtonClick.emit(clicked);
  }

  handleSlideChange(slideChange: number): void {
    this.currentSlideIndex = slideChange;
  }

  handleSlideSwipe(slideIndex: number): void {
    this.currentSlideIndex = slideIndex;
  }

  handleArrowClick(event: Event): void {
    this.mouseClick = event;
  }

  handleClaimingDialogAction(action: ClaimingDialogActions): void {
    this.claimingDialogActionEmitter.emit(action);
  }
}
